export const GOOGLE_MAPS_API_KEY =
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() || undefined;

const TILE_API = 'https://tile.googleapis.com';

export const GOOGLE_LOGO_URL = 'https://maps.gstatic.com/mapfiles/api-3/images/google_white5.png';

// Google publishes satellite tiles up to this zoom over Moldova; Leaflet
// upscales past it instead of requesting tiles that do not exist.
export const GOOGLE_MAX_NATIVE_ZOOM = 19;

export type GoogleSession = { session: string; expiry: number };

/** Pure: `expiry` arrives as epoch seconds in a string. */
export function parseGoogleSession(body: { session?: unknown; expiry?: unknown }): GoogleSession {
  const expiry = Number(body.expiry) * 1000;
  if (typeof body.session !== 'string' || !body.session || !Number.isFinite(expiry)) {
    throw new Error('Google tiles: malformed session response');
  }
  return { session: body.session, expiry };
}

const SESSION_LANGUAGES: Record<string, string> = { ro: 'ro-RO', ru: 'ru-RU', en: 'en-US' };

export async function createGoogleSession(
  key: string,
  uiLanguage: string,
  signal?: AbortSignal,
): Promise<GoogleSession> {
  const res = await fetch(`${TILE_API}/v1/createSession?key=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mapType: 'satellite',
      language: SESSION_LANGUAGES[uiLanguage.slice(0, 2)] ?? 'ro-RO',
      region: 'MD',
    }),
    signal,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(`Google tiles: ${body?.error?.message ?? `HTTP ${res.status}`}`);
  }
  return parseGoogleSession(body);
}

export function googleTileUrl(session: GoogleSession, key: string): string {
  return (
    `${TILE_API}/v1/2dtiles/{z}/{x}/{y}` +
    `?session=${encodeURIComponent(session.session)}&key=${encodeURIComponent(key)}`
  );
}

type Bounds = { north: number; south: number; east: number; west: number };

/** The imagery credits Google requires us to show for the visible area. */
export async function fetchGoogleCopyright(
  session: GoogleSession,
  key: string,
  bounds: Bounds,
  zoom: number,
  signal?: AbortSignal,
): Promise<string> {
  const clampLon = (v: number) => Math.max(-180, Math.min(180, v));
  const clampLat = (v: number) => Math.max(-90, Math.min(90, v));
  const params = new URLSearchParams({
    session: session.session,
    key,
    zoom: String(Math.round(zoom)),
    north: String(clampLat(bounds.north)),
    south: String(clampLat(bounds.south)),
    east: String(clampLon(bounds.east)),
    west: String(clampLon(bounds.west)),
  });
  const res = await fetch(`${TILE_API}/tile/v1/viewport?${params}`, { signal });
  if (!res.ok) throw new Error(`Google viewport: HTTP ${res.status}`);
  const body = (await res.json()) as { copyright?: string };
  return body.copyright?.trim() ?? '';
}

export type CachedGoogleSession = GoogleSession & { keyTag: string; language: string };

/** Last characters of the key, so a rotated key never reuses an old session. */
export function googleKeyTag(key: string): string {
  return key.slice(-6);
}

// Sessions last about two weeks; renew a day early so they never lapse mid-use.
const RENEW_BEFORE_MS = 24 * 60 * 60 * 1000;

/** Pure: a stored session still good for this key and language, else null. */
export function usableCachedSession(
  raw: unknown,
  keyTag: string,
  language: string,
  now: number,
): GoogleSession | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Partial<CachedGoogleSession>;
  if (c.keyTag !== keyTag || c.language !== language) return null;
  if (typeof c.session !== 'string' || typeof c.expiry !== 'number') return null;
  if (c.expiry - RENEW_BEFORE_MS <= now) return null;
  return { session: c.session, expiry: c.expiry };
}
